import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import AppLayout from '../components/layouts/AppLayout';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, ArrowLeft } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoRole, setDemoRole] = useState(null); // 'admin' | 'investor'

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate(user.role === 'admin' ? '/admin' : '/dashboard');
    }
  }, [user, navigate]);

  const handleDemoLogin = async (role) => {
    const isInvestor = role === 'investor';
    const email = isInvestor ? 'investor1@test.com' : 'admin@investplatform.com';
    const password = isInvestor ? 'Test@123' : 'Admin@123';
    
    setDemoRole(role);
    setDemoLoading(true);
    setFormData({ ...formData, email, password });

    // Artificial delay for the premium experience
    setTimeout(async () => {
      try {
        await login(email, password);
        toast.success(`Welcome back, ${isInvestor ? 'Investor' : 'Admin'}!`);
        navigate(isInvestor ? '/dashboard' : '/admin');
      } catch (error) {
        toast.error('Demo login failed');
        setDemoLoading(false);
      }
    }, 2000); // 2 second animation
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await login(formData.email, formData.password);
        toast.success('Welcome back!');
        navigate(response.user.role === 'admin' ? '/admin' : '/dashboard');
      } else {
        const { register } = await import('../App');
        await register(formData.email, formData.password, formData.name, formData.phone);
        toast.success('Account created! Please wait for approval.');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AppLayout>
      <div className="min-h-screen flex items-center justify-center px-6 py-12" data-testid="login-page">
        {/* Demo Loading Overlay */}
        {demoLoading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#050505]/90 backdrop-blur-xl animate-fade-in">
            <div className="text-center space-y-8 max-w-md px-6">
              <div className="relative w-24 h-24 mx-auto">
                {/* Pulsing rings */}
                <div className="absolute inset-0 rounded-full border-2 border-[#D4AF37]/30 animate-ping"></div>
                <div className="absolute inset-0 rounded-full border border-[#D4AF37]/50 animate-pulse"></div>
                
                {/* Center Icon */}
                <div className="absolute inset-2 bg-gradient-to-br from-[#D4AF37] to-[#F59E0B] rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.4)]">
                  {demoRole === 'admin' ? (
                    <span className="text-4xl">🛡️</span>
                  ) : (
                    <span className="text-4xl">💼</span>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <h2 className="font-['Outfit'] text-3xl font-bold bg-gradient-to-r from-white via-[#D4AF37] to-white bg-clip-text text-transparent bg-300% animate-shine">
                  Accessing Secure Portal...
                </h2>
                <div className="text-[#94A3B8] text-lg font-light leading-relaxed">
                  {demoRole === 'admin' ? (
                    <>
                       Welcome, <span className="text-[#D4AF37] font-semibold">Administrator</span>.
                       <br/>Initializing command center...
                    </>
                  ) : (
                    <>
                      Welcome, <span className="text-[#D4AF37] font-semibold">Investor</span>.
                      <br/>Loading your portfolio...
                    </>
                  )}
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-[#D4AF37] animate-progress-loading w-full origin-left"></div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes shine {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes progress-loading {
            0% { transform: scaleX(0); }
            100% { transform: scaleX(1); }
          }
          .bg-300% { background-size: 300% auto; }
        `}</style>

        {/* Cinematic spotlight behind card */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at center, rgba(212,175,55,0.15) 0%, transparent 45%)',
          }}
        ></div>

        {/* Floating gold particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <div
              key={`particle-${i}`}
              className="absolute rounded-full"
              style={{
                width: `${Math.random() * 2 + 2}px`,
                height: `${Math.random() * 2 + 2}px`,
                background: `radial-gradient(circle, rgba(212,175,55,${Math.random() * 0.6 + 0.3}), rgba(212,175,55,0))`,
                boxShadow: `0 0 ${Math.random() * 8 + 4}px rgba(212,175,55,${Math.random() * 0.5 + 0.2})`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float-particle ${Math.random() * 20 + 15}s linear infinite`,
                animationDelay: `${Math.random() * 5}s`,
              }}
            ></div>
          ))}
        </div>

        <style>{`
          @keyframes float-particle {
            0% { transform: translateY(0px) translateX(0px); opacity: 0; }
            10% { opacity: 1; }
            90% { opacity: 1; }
            100% { transform: translateY(-100px) translateX(${Math.random() * 40 - 20}px); opacity: 0; }
          }
        `}</style>

        <button
          onClick={() => navigate('/')}
          className="absolute top-8 left-8 text-[#94A3B8] hover:text-white transition-colors flex items-center gap-2 z-20"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        <div className="w-full max-w-md relative z-10">
        <div
          className="p-8 rounded-2xl relative overflow-hidden"
          style={{
            backdropFilter: 'blur(18px)',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.08) inset, 0 0 40px rgba(212,175,55,0.1)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          {/* Glass reflection overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.02) 50%, transparent 100%)',
              borderRadius: '1rem',
            }}
          ></div>
          {/* Header */}
          <div className="text-center mb-8 relative z-10">
            {/* Exclusive Badge */}
            <div className="inline-block mb-4 px-3 py-1 rounded-full bg-white/8 border border-white/15 backdrop-blur-sm">
              <p className="text-xs font-medium tracking-wide text-[#D4AF37]">EXCLUSIVE INVESTOR PORTAL</p>
            </div>
            <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#F59E0B] rounded-xl mx-auto mb-4 shadow-[0_0_30px_rgba(212,175,55,0.3)]"></div>
            <h1 className="font-['Outfit'] text-3xl font-bold mb-2">
              {isLogin ? 'Welcome back' : 'Create Account'}
            </h1>
            <p className="text-[#94A3B8] text-sm">
              {isLogin ? 'Access your premium investment portfolio' : 'Register for exclusive platform access'}
            </p>
          </div>

          {/* Google OAuth */}
          {/* <button
            onClick={handleGoogleLogin}
            className="w-full py-3 px-4 border border-white/10 rounded-lg hover:bg-white/5 transition-all flex items-center justify-center gap-3 mb-6 relative z-10"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button> */}

          {/* <div className="relative mb-6 z-10">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-transparent text-[#64748B]">or</span>
            </div>
          </div> */}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4 relative z-10">
            {!isLogin && (
              <div data-testid="register-name-field">
                <label className="block text-sm font-medium mb-2">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 focus:bg-white/8 outline-none transition-all"
                  placeholder="John Doe"
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>
            )}

            <div data-testid="email-field">
              <label className="block text-sm font-medium mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 focus:bg-white/8 outline-none transition-all"
                  placeholder="you@example.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div data-testid="password-field">
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-11 pr-12 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 focus:bg-white/8 outline-none transition-all"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {!isLogin && (
              <div data-testid="register-phone-field">
                <label className="block text-sm font-medium mb-2">Phone (Optional)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 focus:bg-white/8 outline-none transition-all"
                  placeholder="+1 (555) 123-4567"
                  data-testid="phone-input"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-gradient-to-r from-[#F5C542] to-[#D4AF37] text-[#0A1128] font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_15px_40px_rgba(212,175,55,0.35)] hover:translate-y-[-2px] active:translate-y-0"
              style={{
                boxShadow: '0 10px 25px rgba(212,175,55,0.25)',
              }}
              data-testid="submit-auth-btn"
            >
              {loading ? 'Processing...' : (isLogin ? 'Login' : 'Create Account')}
            </button>
          </form>

          {/* Toggle */}
          <div className="mt-6 text-center text-sm relative z-10">
            <span className="text-[#94A3B8]">
              {isLogin ? "Don't have an account?" : "Already have an account?"}
            </span>
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="ml-2 text-[#D4AF37] hover:underline font-medium"
              data-testid="toggle-auth-mode-btn"
            >
              {isLogin ? 'Register' : 'Login'}
            </button>
          </div>

            {/* Quick Login for Demo */}
            {isLogin && (
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-xs text-center text-[#94A3B8] mb-4 uppercase tracking-wider font-semibold">Demo Access</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleDemoLogin('admin')}
                    className="px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-lg text-[#D4AF37] text-xs font-bold hover:bg-[#D4AF37]/20 transition-all flex flex-col items-center gap-1 group"
                  >
                    <span className="group-hover:scale-110 transition-transform">🛡️</span>
                    <span>Admin</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDemoLogin('investor')}
                    className="px-4 py-2 bg-[#3B82F6]/10 border border-[#3B82F6]/20 rounded-lg text-[#3B82F6] text-xs font-bold hover:bg-[#3B82F6]/20 transition-all flex flex-col items-center gap-1 group"
                  >
                    <span className="group-hover:scale-110 transition-transform">💼</span>
                    <span>Investor</span>
                  </button>
                </div>
              </div>
            )}
            
            {/* Apply Link - Moved down */}
            <div className="mt-6 text-center text-sm relative z-10">
              <span className="text-[#94A3B8]">
                New investor?
              </span>
              <button
                onClick={() => navigate('/apply')}
                className="ml-2 text-[#D4AF37] hover:underline font-medium"
                data-testid="goto-apply-btn"
              >
                Apply for Access
              </button>
            </div>
        </div>
      </div>
      </div>
    </AppLayout>
  );
}
